import type {
  BillBand,
  BillCount,
  FaIncomeInput,
  FaRead,
} from "@medbillai/schemas";
import {
  type NateraIntakeProgram as IntakeProgram,
  NATERA_INTAKE_PROGRAMS,
} from "@medbillai/schemas/nateraIntakePacket";
import {
  NATERA_EXPRESS_VARIANTS,
  type NateraExpressVariant,
  type NateraIntakePlaybookVariant,
  type NateraIntakeStoryVariant,
} from "@medbillai/utils/flag";

import type { Feeling, StressLevel } from "./persona";

export type {
  BillBand,
  BillCount,
  FaIncomeInput,
  FaRead,
  IntakeProgram,
  NateraExpressVariant,
  NateraIntakePlaybookVariant,
  NateraIntakeStoryVariant,
};

// Milestone types, composed with `&` into each screen's `needs`. The flow engine
// accumulates them forward, so a screen declares everything collected up to it.

export type BillCountData = {
  billCount: BillCount;
};

export type BillData = BillCountData & {
  /** The range the member picked; explicit null when they skipped it. */
  billBand: BillBand | null;
};

/** Which bill brought the member in. Only a lab program's bill continues past validation. */
export const BILL_SOURCES = [
  "natera",
  "bto",
  "other-lab",
  "something-else",
] as const;
export type BillSource = (typeof BILL_SOURCES)[number];

export type BillSourceData = {
  billSource: BillSource;
};

export type StressData = {
  stressLevel: StressLevel;
};

export type FeelingData = {
  feeling: Feeling;
};

/** Everything the persona layer collects, composed into the screens downstream of it. */
export type EmotionalData = StressData & FeelingData;

/**
 * The playbook experiment arm, raw, so a resumed session stays in it. `null` when
 * the member passed the fork before flags were ready and so stays unenrolled;
 * absent before the fork.
 */
export type PlaybookArmData = {
  playbookArm?: NateraIntakePlaybookVariant | null;
};

/** The story experiment arm, with the same null/absent meaning as `PlaybookArmData`. */
export type StoryArmData = {
  storyArm?: NateraIntakeStoryVariant | null;
};

/** Null/absent as in `PlaybookArmData`. */
export type ExpressArmData = {
  expressArm?: NateraExpressVariant | null;
};

/** The lab program the member's bill belongs to; the lander's, or the brand they picked. */
export type ProgramData = {
  program: IntakeProgram;
};

export const isIntakeProgram = (value: string): value is IntakeProgram =>
  NATERA_INTAKE_PROGRAMS.some(candidate => candidate === value);

/** The packet's program, which the API returns as a plain string. */
export const programOf = (program: string | null | undefined): IntakeProgram =>
  NATERA_INTAKE_PROGRAMS.find(candidate => candidate === program) ?? "natera";

export type ExpressServedArm = Extract<
  NateraExpressVariant,
  "express" | "experiment-express"
>;

/** The packet's arm, which the API returns as a plain string. */
export const expressArmOf = (arm: string | null | undefined) =>
  NATERA_EXPRESS_VARIANTS.find(variant => variant === arm);

/** Where the hero questions were answered. */
export type LandingEntry = "offsite" | "onsite" | "routing";

export type LandingEntryData = {
  landingEntry?: LandingEntry;
};

export type HouseholdData = {
  householdSize: number;
};

export type IncomeData = {
  income: FaIncomeInput;
};

export type FaReadData = {
  faRead: FaRead;
};

export type StoryData = {
  /** Explicit null when skipped — the packet never omits a skipped answer. */
  story: string | null;
};
